import { useEffect, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { priceLabel, SIGNAL_EVENT_LABEL, type SignalTableSort } from '@vtf/shared';
import { api, errorText } from '../lib/api';
import { Card, cx, Empty, ErrorText, Pill, Segmented, Select } from './ui';
import { isFlashing, SIGNAL_TONE, SignalFeedItem, useFlashClock, type SignalFeedEntry, type SignalView } from './SignalFeedItem';

/**
 * Floor → Signals → Table (INT-SYN-02): TradingView strategy signals as sortable rows, like the owner's spreadsheet.
 * Sorting, the latest-per-symbol view and the "now" bar counts come from the API; the current price is the interim
 * delayed price with its source and time (INT-SYN-04, D23), never a TradingView price (D22). The query key starts with
 * 'feed', so the floor's realtime `feed.item` event refreshes it when a new signal arrives.
 */

export interface SignalTableRow extends SignalView {
  exchange: string | null;
  timezone: string;
  barTime: string;
  fsb: 'BLUE' | 'RED' | 'YELLOW' | 'NOBLE' | null;
  fsbLabel: string | null;
  barsAway: number | null;
  barsAwayAtSignal: number | null;
  royal: 'LAST' | 'SECOND_LAST' | null;
  royalLabel: string | null;
  royalBarsAgo: number | null;
  royalBarsAgoAtSignal: number | null;
  confidence: 'HIGH' | 'MEDIUM' | 'LOW' | null;
  breakoutBarsAgo: number | null;
  breakoutBarsAgoAtSignal: number | null;
  currentPrice: { price: string; source: string; sourceName: string; sourceSymbol: string; proxyNote: string | null; asOf: string; ageMinutes: number; stale: boolean } | null;
  canMakeSetup: boolean;
  mySetupId: string | null;
}
interface TableData {
  view: 'latest' | 'all';
  total: number;
  truncated: boolean;
  rows: SignalTableRow[];
  options: { strategies: string[]; timeframes: string[]; events: SignalView['event'][] };
}

type Dir = 'asc' | 'desc';
const FSB_CLS: Record<NonNullable<SignalTableRow['fsb']>, string> = {
  BLUE: 'border-[#2b4a78] bg-accent-bg text-accent',
  RED: 'border-[#6a2a27] bg-down-bg text-down',
  YELLOW: 'border-[#5a4316] bg-warn-bg text-warn',
  NOBLE: 'border-[#3a2f66] bg-[#221b3d] text-live',
};
const VOLUME_TONE = { HIGH: 'up', MEDIUM: 'warn', LOW: 'neutral' } as const;

/** Exchange time with its zone abbreviation, e.g. "Mon 2 Nov 22:00 EST" (New York) or "… GMT+5" (Karachi). */
export function exchangeTime(iso: string, tz: string) {
  const d = new Date(iso);
  const main = new Intl.DateTimeFormat('en-GB', { timeZone: tz, weekday: 'short', day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }).format(d).replace(',', '');
  const zone = new Intl.DateTimeFormat('en-US', { timeZone: tz, timeZoneName: 'short' }).formatToParts(d).find((p) => p.type === 'timeZoneName')?.value ?? tz;
  return { main, zone };
}

/** First click on a column: newest / strongest / nearest first where that is what a trader looks for. */
const FIRST_DIR: Partial<Record<SignalTableSort, Dir>> = { time: 'desc', received: 'desc', confidence: 'desc', price: 'desc', signalPrice: 'desc' };

function SortHeader({ col, label, sort, dir, onSort, className, title }: { col: SignalTableSort; label: string; sort: SignalTableSort; dir: Dir; onSort: (c: SignalTableSort) => void; className?: string; title?: string }) {
  const on = sort === col;
  return (
    <th scope="col" aria-sort={on ? (dir === 'asc' ? 'ascending' : 'descending') : 'none'} className={cx('border-b border-line px-2.5 py-2 text-left align-bottom font-normal whitespace-nowrap', className)}>
      <button type="button" onClick={() => onSort(col)} title={title} className={cx('inline-flex cursor-pointer items-center gap-1 border-0 bg-transparent p-0 text-xs tracking-[0.04em] uppercase', on ? 'font-semibold text-ink' : 'text-muted hover:text-ink')}>
        {label}
        <span aria-hidden="true" className={cx('text-[10px]', !on && 'opacity-0')}>
          {on && dir === 'asc' ? '▲' : '▼'}
        </span>
      </button>
    </th>
  );
}

/** "6" with the signal's own value: "(4 at signal)". */
function Bars({ now, atSignal }: { now: number | null; atSignal: number | null }) {
  if (atSignal === null) return <span className="text-muted">—</span>;
  return (
    <span className="flex flex-col leading-tight">
      <span className="font-mono font-semibold">{now ?? '—'}</span>
      <span className="text-[11px] text-muted">{atSignal} at signal</span>
    </span>
  );
}

const VIEW_KEY = 'vtf.floor.signalTable.view';
const readView = (): 'latest' | 'all' => {
  try {
    return localStorage.getItem(VIEW_KEY) === 'all' ? 'all' : 'latest';
  } catch {
    return 'latest';
  }
};

export function SignalTable({ market, tz }: { market: string; tz: string }) {
  const [view, setViewState] = useState<'latest' | 'all'>(readView);
  const setView = (v: 'latest' | 'all') => {
    setViewState(v);
    try {
      localStorage.setItem(VIEW_KEY, v);
    } catch {
      /* private window: just not remembered */
    }
  };
  const [tf, setTf] = useState('');
  const [strategy, setStrategy] = useState('');
  const [event, setEvent] = useState('');
  const [sort, setSort] = useState<SignalTableSort>('time');
  const [dir, setDir] = useState<Dir>('desc');
  const [selected, setSelected] = useState<string | null>(null);
  const detailRef = useRef<HTMLDivElement>(null);

  const qs = new URLSearchParams({ view, sort, dir, limit: '300', ...(market ? { market } : {}), ...(tf ? { tf } : {}), ...(strategy ? { strategy } : {}), ...(event ? { event } : {}) });
  const q = useQuery({
    queryKey: ['feed', 'signal-table', view, market, tf, strategy, event, sort, dir],
    queryFn: () => api<TableData>(`/signals/table?${qs}`),
    // new signals arrive over the realtime channel (feed.item); this only keeps the "now" bar counts and prices fresh
    refetchInterval: 60_000,
    placeholderData: (prev) => prev,
  });
  const rows = q.data?.rows ?? [];
  const opts = q.data?.options;
  const now = useFlashClock(rows.map((r) => r.flashUntil));
  const multiStrategy = (opts?.strategies.length ?? 0) > 1;
  const chosen = rows.find((r) => r.id === selected) ?? null;

  useEffect(() => {
    if (chosen) detailRef.current?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
  }, [chosen?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const onSort = (c: SignalTableSort) => {
    if (c === sort) setDir(dir === 'asc' ? 'desc' : 'asc');
    else {
      setSort(c);
      setDir(FIRST_DIR[c] ?? 'asc');
    }
  };
  const head = { sort, dir, onSort };
  const cell = 'border-b border-line px-2.5 py-2 align-middle whitespace-nowrap';

  return (
    <section aria-label="Signals table" className="flex min-w-0 flex-col gap-3">
      <div className="flex flex-wrap items-end gap-2">
        <Segmented
          label="Rows"
          value={view}
          onChange={setView}
          options={[
            { value: 'latest', label: 'Latest per symbol' },
            { value: 'all', label: 'Every signal' },
          ]}
        />
        <div className="w-[calc(50%-4px)] sm:w-40">
          <Select aria-label="Timeframe" value={tf} onChange={(e) => setTf(e.target.value)}>
          <option value="" label="All timeframes" />
          {(opts?.timeframes ?? []).map((t) => (
            <option key={t} value={t} label={t} />
          ))}
          </Select>
        </div>
        {multiStrategy && (
          <div className="w-[calc(50%-4px)] sm:w-44">
          <Select aria-label="Strategy" value={strategy} onChange={(e) => setStrategy(e.target.value)}>
            <option value="" label="All strategies" />
            {opts!.strategies.map((s) => (
              <option key={s} value={s} label={s} />
            ))}
          </Select>
          </div>
        )}
        <div className="w-[calc(50%-4px)] sm:w-40">
          <Select aria-label="Event" value={event} onChange={(e) => setEvent(e.target.value)}>
          <option value="" label="All events" />
          {(opts?.events ?? []).map((e) => (
            <option key={e} value={e.toLowerCase()} label={SIGNAL_EVENT_LABEL[e]} />
          ))}
          </Select>
        </div>
      </div>

      <ErrorText error={q.error ? errorText(q.error) : null} />
      {q.isLoading && <Card className="p-4 text-sub">Loading…</Card>}
      {!q.isLoading && !q.error && !rows.length && (
        <Card>
          <Empty>No signals match these filters.</Empty>
        </Card>
      )}
      {rows.length > 0 && (
        <div className="max-w-full overflow-x-auto rounded-xl border border-line bg-panel" data-testid="signal-table-scroll">
          <table className="w-max min-w-full border-separate border-spacing-0 text-[13px]">
            <caption className="sr-only">
              TradingView strategy signals, {view === 'latest' ? 'the latest per symbol, timeframe and strategy' : 'every signal'}, sorted by {sort} {dir === 'asc' ? 'ascending' : 'descending'}
            </caption>
            <thead>
              <tr>
                <SortHeader col="symbol" label="Symbol" {...head} className="sticky left-0 z-20 bg-panel" />
                <SortHeader col="exchange" label="Exchange" {...head} />
                <SortHeader col="price" label="Current price" {...head} title="Delayed public quote with its source and time (never a TradingView price)" />
                <SortHeader col="direction" label="Signal" {...head} />
                <SortHeader col="barsAway" label="Bars away" {...head} title="Bars from the FSB, counted to now" />
                <SortHeader col="fsb" label="Signal type" {...head} />
                <SortHeader col="confidence" label="Volume" {...head} title="Volume significance of the breakout" />
                <SortHeader col="breakout" label="Breakout bars ago" {...head} title="Bars from the breakout candle, counted to now" />
                <SortHeader col="event" label="Event" {...head} />
                <SortHeader col="tf" label="TF" {...head} />
                <SortHeader col="time" label="Signal time" {...head} title="The signal bar's time in the exchange's own time zone" />
                {multiStrategy && <SortHeader col="strategy" label="Strategy" {...head} />}
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => {
                const flashing = isFlashing(r, now);
                const t = exchangeTime(r.barTime, r.timezone);
                const isSel = r.id === selected;
                return (
                  <tr key={r.id} data-signal-id={r.id} data-flashing={flashing ? 'true' : undefined} data-selected={isSel ? 'true' : undefined} onClick={() => setSelected(isSel ? null : r.id)} className={cx('cursor-pointer hover:[&>td]:bg-raised', flashing && 'signal-flash-row', isSel && '[&>td]:bg-raised')}>
                    <td className={cx(cell, 'sticky left-0 z-10 bg-panel font-semibold')}>
                      <button type="button" onClick={(e) => (e.stopPropagation(), setSelected(isSel ? null : r.id))} aria-expanded={isSel} aria-controls="signal-table-detail" aria-label={`${r.instrument.symbol} ${r.timeframe} ${r.strategy}: ${isSel ? 'hide' : 'open'} signal`} className="cursor-pointer border-0 bg-transparent p-0 text-left font-semibold text-ink hover:text-accent">
                        {r.instrument.symbol}
                      </button>
                      {flashing && (
                        <Pill tone="accent" className="ml-1.5 px-1.5 py-0 text-[10px] uppercase">
                          New
                        </Pill>
                      )}
                    </td>
                    <td className={cx(cell, 'text-sub')}>{r.exchange ?? '—'}</td>
                    <td className={cell}>
                      {r.currentPrice ? (
                        <span className="flex flex-col leading-tight" title={`${r.currentPrice.sourceName} public quote${r.currentPrice.proxyNote ? ` (${r.currentPrice.proxyNote})` : ''} — delayed, not live. Time in ${tz}.`}>
                          <span className="font-mono font-semibold">{r.currentPrice.price}</span>
                          <span className={cx('text-[11px]', r.currentPrice.stale ? 'text-warn' : 'text-muted')}>{priceLabel(r.currentPrice, tz)}</span>
                        </span>
                      ) : (
                        <span className="text-muted" title="No delayed price for this symbol">
                          —
                        </span>
                      )}
                    </td>
                    <td className={cx(cell, 'font-semibold', r.direction === 'LONG' ? 'text-up' : r.direction === 'SHORT' ? 'text-down' : 'text-muted')}>{r.direction === 'LONG' ? '▲ Long' : r.direction === 'SHORT' ? '▼ Short' : '—'}</td>
                    <td className={cell}>
                      <Bars now={r.barsAway} atSignal={r.barsAwayAtSignal} />
                    </td>
                    <td className={cell}>{r.fsb ? <span className={cx('inline-flex rounded-full border px-2.5 py-0.5 text-xs font-semibold', FSB_CLS[r.fsb])}>{r.fsbLabel}</span> : <span className="text-muted">—</span>}</td>
                    <td className={cell}>{r.confidence ? <Pill tone={VOLUME_TONE[r.confidence]}>{r.confidence}</Pill> : <span className="text-muted">—</span>}</td>
                    <td className={cell}>
                      <Bars now={r.breakoutBarsAgo} atSignal={r.breakoutBarsAgoAtSignal} />
                    </td>
                    <td className={cell}>
                      <Pill tone={SIGNAL_TONE[r.event]}>{r.eventLabel}</Pill>
                    </td>
                    <td className={cx(cell, 'font-mono')}>{r.timeframe}</td>
                    <td className={cell}>
                      <span className="flex flex-col leading-tight" title={`${r.timezone}${r.signalTime ? '' : ' · the script sent no bar time: time received'}`}>
                        <span>{t.main}</span>
                        <span className="text-[11px] text-muted">{t.zone}</span>
                      </span>
                    </td>
                    {multiStrategy && <td className={cx(cell, 'text-sub')}>{r.strategy}</td>}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      {q.data && rows.length > 0 && (
        <span className="text-xs text-muted">
          {q.data.total > rows.length ? `Showing ${rows.length} of ${q.data.total}` : `${rows.length} ${rows.length === 1 ? 'row' : 'rows'}`}
          {q.data.truncated ? ' · the newest 5,000 signals' : ''} · bar counts are to now (the signal’s own value underneath) · prices are delayed public quotes · select a row to open the signal
        </span>
      )}
      <div ref={detailRef} id="signal-table-detail" aria-live="polite">
        {chosen && (
          <div className="flex flex-col gap-2">
            <div className="flex items-center justify-between">
              <span className="text-xs tracking-[0.06em] text-muted uppercase">Selected signal</span>
              <button type="button" onClick={() => setSelected(null)} className="cursor-pointer border-0 bg-transparent text-[13px] font-semibold text-accent">
                Close
              </button>
            </div>
            <SignalFeedItem item={{ id: chosen.id, type: 'SIGNAL', createdAt: chosen.receivedAt, market: null, signal: chosen } satisfies SignalFeedEntry} tz={tz} now={now} />
          </div>
        )}
      </div>
    </section>
  );
}
